'use client';
import { MultiServicePreset } from '@/types';

import Button from '@/components/Button';
import { useConfig } from '@/app/contexts/ConfigContext';
import CustomLink from '@/components/CustomLink';
import { SERVICE_TYPES } from '@/helpers/enums';
import { URL_S3 } from '@/helpers/constants';
import type { CSSProperties } from 'react';

type HeroStyle = CSSProperties & {
  '--hero-mobile': string;
  '--hero-desktop': string;
};

export default function Hero() {
  const { wording, config } = useConfig();
  const isMultiFitnessYogaPilates = config.multiService === MultiServicePreset.FYP;
  const isMultiAstrologyMeditationLove =
    config.multiService === MultiServicePreset.AML;
  const isMultiPortal =
    isMultiFitnessYogaPilates || isMultiAstrologyMeditationLove;

  const heroImage = (type: 'desktop' | 'mobile') => {
    const heroDesktopImage = () => {
      if (isMultiFitnessYogaPilates) {
        return `${URL_S3}/assets/images/hero-multiFitnessYogaPilates.webp`;
      } else if (isMultiAstrologyMeditationLove) {
        return `${URL_S3}/assets/images/hero-multiAstrologyMeditationLove.webp`;
      } else {
        return `${URL_S3}/${config.serviceType}/images/hero.webp`;
      }
    };

    const heroMobileImage = () => {
      if (isMultiFitnessYogaPilates) {
        return `${URL_S3}/assets/images/hero-mobile-multiFitnessYogaPilates.webp`;
      } else if (isMultiAstrologyMeditationLove) {
        return `${URL_S3}/assets/images/hero-mobile-multiAstrologyMeditationLove.webp`;
      } else {
        return `${URL_S3}/${config.serviceType}/images/hero-mobile.webp`;
      }
    };

    let heroImage = '';
    if (type === 'mobile') {
      heroImage = heroMobileImage();
    } else if (type === 'desktop') {
      heroImage = heroDesktopImage();
    }

    return heroImage;
  };

  const heroBgStyle: HeroStyle = {
    '--hero-mobile': `url(${heroImage('mobile')})`,
    '--hero-desktop': `url(${heroImage('desktop')})`,
  };

  return (
    <div
      className='
        relative
        w-full
        max-w-[390px] sm:max-w-[1710px]
        mx-auto
        aspect-[390/208] sm:aspect-[1710/655]
        overflow-hidden bg-no-repeat bg-center bg-cover
        bg-[image:var(--hero-mobile)]
        sm:bg-[image:var(--hero-desktop)]
      '
      style={heroBgStyle}
    >
      <div
        className={`
          absolute inset-0 z-10 flex flex-col justify-center gap-2 py-2 
          ${isMultiPortal ? 'w-[55%] xl:w-[45%]' : ''}
          ${isMultiPortal ? 'pl-8 xl:pl-16' : 'px-3 lg:px-32'}
        `}
      >
        <div
          className={`${
            isMultiPortal
              ? 'text-[clamp(1.25rem,5.5vw,1.75rem)] leading-[1.05]'
              : 'text-[clamp(1.25rem,5.5vw,1.75rem)] leading-[1.05]'
          } xl:text-[clamp(2.5rem,4vw,5rem)] xl:leading-[1.08] font-extrabold`}
          style={{
            color: isMultiPortal
              ? config.theme.textColorDark
              : config.theme.textColorLight,
            // ...textLanguageSpecificStyle(),
          }}
        >
          {wording?.hero_text}
        </div>

        <Button
          className='self-start !px-3 !py-2 !text-sm !leading-tight sm:!px-4 sm:!py-2 sm:!text-lg xl:!px-4 xl:!py-3 xl:!text-[22px]'
          themeTextColor={config.theme.textColorLight}
          themeBackgroundColor={config.theme.accentColorOne}
        >
          <CustomLink
            href={
              config.serviceType === SERVICE_TYPES.FITNESS
                ? '/media/media_2'
                : '/media/media_all'
            }
          >
            {wording?.hero_button_text}
          </CustomLink>
        </Button>
      </div>
    </div>
  );
}
